import { useState, useEffect } from "react";
import { login, getSiteToken, clearSiteToken, authedFetch } from "./api";

export default function SiteGate({ children }) {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Verify any stored token is still valid on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getSiteToken()) {
        if (!cancelled) { setAuthed(false); setChecking(false); }
        return;
      }
      try {
        const res = await authedFetch("site", "/api/auth/me");
        if (!cancelled) {
          if (res.ok) setAuthed(true);
          else { clearSiteToken(); setAuthed(false); }
          setChecking(false);
        }
      } catch {
        if (!cancelled) { clearSiteToken(); setAuthed(false); setChecking(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.body.style.overflow = authed || checking ? "" : "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [authed, checking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    const { ok, error: msg } = await login("site", pin.trim());
    setSubmitting(false);
    if (ok) {
      setAuthed(true);
    } else {
      setError(msg || "That code doesn't match. Please try again.");
      setPin("");
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  };

  if (checking) {
    return (
      <div className="site-gate" data-testid="site-gate-loading">
        <div className="site-gate-bg" />
      </div>
    );
  }

  if (authed) return children;

  return (
    <div className="site-gate" data-testid="site-gate">
      <div className="site-gate-bg" />
      <div className={`site-gate-card ${shaking ? 'shake' : ''}`}>
        <div className="site-gate-monogram">N&nbsp;&amp;&nbsp;S</div>
        <div className="site-gate-eyebrow">By invitation only</div>
        <h1 className="site-gate-title">Nathan &amp; Sandra</h1>
        <p className="site-gate-sub">Please enter the access code shared with you.</p>

        <form onSubmit={handleSubmit} className="site-gate-form">
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="8"
            className="site-gate-input"
            placeholder="••••"
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setError(""); }}
            autoFocus
            data-testid="site-gate-input"
          />
          {error && <p className="site-gate-error" data-testid="site-gate-error">{error}</p>}
          <button type="submit" className="site-gate-btn" data-testid="site-gate-submit" disabled={submitting}>
            {submitting ? "Checking…" : "Enter →"}
          </button>
        </form>

        <p className="site-gate-footnote">
          Don't have a code? Please contact Nathan or Sandra.
        </p>
      </div>

      <style>{`
        .site-gate {
          position: fixed; inset: 0;
          display: flex; align-items: center; justify-content: center;
          z-index: 99999;
          padding: 1.5rem;
          font-family: 'Cormorant Garamond', Georgia, serif;
        }
        .site-gate-bg {
          position: absolute; inset: 0;
          background:
            radial-gradient(1200px 800px at 20% 10%, rgba(138,155,122,0.10), transparent),
            radial-gradient(1000px 600px at 80% 90%, rgba(138,155,122,0.08), transparent),
            linear-gradient(180deg, #0e120a 0%, #171b12 100%);
          z-index: -1;
        }
        .site-gate-card {
          background: rgba(20, 24, 16, 0.85);
          backdrop-filter: blur(14px);
          border: 1px solid rgba(203, 208, 192, 0.14);
          border-radius: 12px;
          padding: 3rem 2.25rem;
          max-width: 400px;
          width: 100%;
          text-align: center;
          box-shadow: 0 30px 80px rgba(0,0,0,0.5);
          color: #f4f1eb;
        }
        .site-gate-card.shake {
          animation: gate-shake 0.45s ease;
        }
        @keyframes gate-shake {
          10%, 90% { transform: translateX(-3px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-6px); }
          40%, 60% { transform: translateX(6px); }
        }
        .site-gate-monogram {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2rem;
          letter-spacing: 0.35em;
          color: #cbd0c0;
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(203, 208, 192, 0.15);
        }
        .site-gate-eyebrow {
          font-size: 11px;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: #a5b094;
          margin-bottom: 1rem;
        }
        .site-gate-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.25rem;
          font-weight: 500;
          margin: 0 0 0.5rem;
          letter-spacing: 0.04em;
          color: #f4f1eb;
        }
        .site-gate-sub {
          font-size: 1rem;
          color: rgba(255,255,255,0.65);
          margin: 0 0 2rem;
          font-style: italic;
        }
        .site-gate-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .site-gate-input {
          width: 100%;
          padding: 1rem;
          font-size: 1.75rem;
          text-align: center;
          letter-spacing: 0.7rem;
          border: 1px solid rgba(203, 208, 192, 0.25);
          border-radius: 6px;
          background: rgba(255,255,255,0.05);
          color: #f4f1eb;
          font-family: 'Cormorant Garamond', serif;
          transition: all 0.15s ease;
        }
        .site-gate-input:focus {
          outline: none;
          border-color: #a5b094;
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(138, 155, 122, 0.15);
        }
        .site-gate-input::placeholder { color: rgba(255,255,255,0.25); }
        .site-gate-error {
          color: #d4938c;
          font-size: 0.9rem;
          margin: 0;
          font-style: italic;
        }
        .site-gate-btn {
          background: #8a9b7a;
          color: #0f120b;
          border: none;
          padding: 1rem 1.5rem;
          font-size: 0.85rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 700;
          transition: background 0.15s ease;
          font-family: inherit;
        }
        .site-gate-btn:hover { background: #a5b094; }
        .site-gate-footnote {
          margin-top: 2rem;
          font-size: 0.85rem;
          color: rgba(255,255,255,0.4);
          font-style: italic;
        }
      `}</style>
    </div>
  );
}
